
import { supabase } from "@/integrations/supabase/client";
import type { DocumentWithRelations } from './documentTypes';

// Document CRUD operations
export const fetchDocuments = async (): Promise<DocumentWithRelations[]> => {
  try {
    // Get documents with organization and contact info
    const { data, error } = await supabase
      .from('documents')
      .select(`
        *,
        organization:organization_id (
          id, name
        ),
        contact:contact_id (
          id, first_name, last_name, email
        )
      `)
      .order('created_at', { ascending: false });

    if (error) throw error;
    
    if (!data || data.length === 0) {
      return [];
    }

    // Get unique uploaded_by user IDs and fetch from profiles
    const uploaderIds = [...new Set(data.filter(doc => doc.uploaded_by).map(doc => doc.uploaded_by))];
    
    let profiles: any[] = [];
    if (uploaderIds.length > 0) {
      const { data: profilesData, error: profilesError } = await supabase
        .from('profiles')
        .select('id, first_name, last_name, email')
        .in('id', uploaderIds);

      if (!profilesError) {
        profiles = profilesData || [];
      }
    }

    // Combine documents with uploader profile data
    return data.map(doc => ({
      ...doc,
      uploaded_by_user: profiles.find(profile => profile.id === doc.uploaded_by) || null
    })) as DocumentWithRelations[];
  } catch (error) {
    throw error;
  }
};

export const fetchDocumentById = async (id: string): Promise<DocumentWithRelations> => {
  try {
    // Get document with organization and contact info
    const { data, error } = await supabase
      .from('documents')
      .select(`
        *,
        organization:organization_id (
          id, name
        ),
        contact:contact_id (
          id, first_name, last_name, email
        )
      `)
      .eq('id', id)
      .maybeSingle();

    if (error) throw error;
    if (!data) throw new Error('Document not found');

    // Get uploader profile information if available
    let uploaded_by_user = null;
    if (data.uploaded_by) {
      const { data: profileData, error: profileError } = await supabase
        .from('profiles')
        .select('id, first_name, last_name, email')
        .eq('id', data.uploaded_by)
        .maybeSingle();

      if (!profileError && profileData) {
        uploaded_by_user = profileData;
      }
    }

    return {
      ...data,
      uploaded_by_user
    } as DocumentWithRelations;
  } catch (error) {
    throw error;
  }
};

export const deleteDocument = async (id: string): Promise<boolean> => {
  try {
    const { error } = await supabase
      .from('documents')
      .delete()
      .eq('id', id);

    if (error) throw error;
    return true;
  } catch (error) {
    throw error;
  }
};
